<template>
    <v-card>
        <v-data-table
            :loading="reportTableLoading"
            :headers="headers"
            :items="pendingCardsReportData"
            :hide-default-footer="hideFooter"
            class="elevation-1"
        >
            <template v-slot:item.total_value="{item}">
                {{ getValueInReais(item.total_value) }}
            </template>
            <template v-slot:item.check_in="{item}">
                {{ formatDate(item.check_in) }}
            </template>
            <template v-slot:item.check_out="{item}">
                {{ formatDate(item.check_out) }}
            </template>
            <template v-slot:item.payment_date="{item}">
                {{ formatDate(item.payment_date) }}
            </template>
            <template v-slot:item.credit_card_number="{item}">
                {{ maskCardNumber(item.credit_card_number) }}
            </template>
            <template v-slot:item.card_status="{item}">
                <v-chip
                    :color="getStatusColor(item.card_status)"
                    text-color="white"
                    small
                >
                    {{ getStatusText(item.card_status) }}
                </v-chip>
            </template>
            <template v-slot:item.payment_status="{item}">
                <v-chip
                    :color="getPaymentStatusColor(item.payment_status)"
                    text-color="white"
                    small
                >
                    {{ getPaymentStatusText(item.payment_status) }}
                </v-chip>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
export default {
    name: "PendingCardsReportTable",

    props: {
        hideFooter: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        pendingCardsReportData: [],
        reportTableLoading: true,
        headers: [
            {text: 'ID Reserva', value: 'id', width: '8%'},
            {text: 'Hotel ID', value: 'hotel_id', width: '8%'},
            {text: 'Valor Total', value: 'total_value', width: '10%'},
            {text: 'Check-in', value: 'check_in', width: '10%'},
            {text: 'Check-out', value: 'check_out', width: '10%'},
            {text: 'ID Cartão', value: 'card_id', width: '15%'},
            {text: 'Validade', value: 'expiration_date', width: '8%'},
            {text: 'Status do Cartão', value: 'card_status', width: '12%'},
            {text: 'Status Pagamento', value: 'payment_status', width: '12%'},
            {text: 'Data Pagamento', value: 'payment_date', width: '10%'},
        ]
    }),

    mounted() {
        this.getReportData();
    },

    methods: {
        async getReportData() {
            this.reportTableLoading = true;
            try {
                const response = await this.$http.get('/api/reports/pending-cards');
                this.pendingCardsReportData = response.data;
                this.$emit('data-loaded', this.pendingCardsReportData.length > 0);
            } catch (error) {
                console.error('Erro ao carregar dados do relatório:', error);
                this.$toast.error('Erro ao carregar dados do relatório');
                this.$emit('data-loaded', false);
            } finally {
                this.reportTableLoading = false;
            }
        },

        getValueInReais(value) {
            if (!value) return 'R$ 0,00';
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(value);
        },

        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('pt-BR');
        },

        maskCardNumber(cardNumber) {
            if (!cardNumber) return '-';
            return cardNumber.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '**** **** **** $4');
        },

        getStatusColor(status) {
            const statusColors = {
                'active': 'green',
                'blocked': 'red',
                'cancelled': 'grey',
                'pending': 'orange'
            };
            return statusColors[status] || 'grey';
        },

        getStatusText(status) {
            const statusTexts = {
                'active': 'Ativo',
                'blocked': 'Bloqueado',
                'cancelled': 'Cancelado',
                'pending': 'Pendente'
            };
            return statusTexts[status] || status;
        },

        getPaymentStatusColor(status) {
            const statusColors = {
                'paid': 'green',
                'pending': 'orange',
                'failed': 'red',
                'cancelled': 'grey'
            };
            return statusColors[status] || 'grey';
        },

        getPaymentStatusText(status) {
            const statusTexts = {
                'paid': 'Pago',
                'pending': 'Pendente',
                'failed': 'Falhou',
                'cancelled': 'Cancelado'
            };
            return statusTexts[status] || status;
        },

        exportToCsv() {
            if (!this.pendingCardsReportData.length) {
                this.$toast.warning('Não há dados para exportar');
                return;
            }

            // Preparar dados para CSV
            const csvData = this.pendingCardsReportData.map(item => ({
                'ID Reserva': item.id,
                'Hotel ID': item.hotel_id,
                'Quarto ID': item.room_id,
                'Tarifa ID': item.fare_id,
                'Valor Total': item.total_value,
                'Check-in': this.formatDate(item.check_in),
                'Check-out': this.formatDate(item.check_out),
                'ID Sistema': item.order_id_system,
                'Status Reserva': item.status,
                'ID Pagamento': item.id_bpayments,
                'ID Cartão': item.card_id,
                'Número do Cartão': item.credit_card_number,
                'Data Validade': item.expiration_date,
                'Status do Cartão': this.getStatusText(item.card_status),
                'Provedor': item.card_provider,
                'Status Pagamento': this.getPaymentStatusText(item.payment_status),
                'Data Pagamento': this.formatDate(item.payment_date)
            }));

            // Converter para CSV
            this.downloadCsv(csvData, 'relatorio_cartoes_pendentes');
        },

        downloadCsv(data, filename) {
            if (!data.length) return;

            // Obter cabeçalhos
            const headers = Object.keys(data[0]);

            // Criar conteúdo CSV
            let csv = headers.join(',') + '\n';

            data.forEach(row => {
                const values = headers.map(header => {
                    const value = row[header];
                    // Escapar aspas e envolver em aspas se necessário
                    if (value && typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                        return '"' + value.replace(/"/g, '""') + '"';
                    }
                    return value;
                });
                csv += values.join(',') + '\n';
            });

            // Criar e baixar arquivo
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');

            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }
}
</script>

<style scoped>
.v-data-table {
    font-size: 14px;
}
</style>
